import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Trophy,
  CalendarDays,
  AlertTriangle,
  Loader2,
  ChevronRight,
  MapPin,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type Team = {
  id: string;
  team_name: string;
};

type Match = {
  id: string;
  date: string;
  team_id: string;
  opponent: string | null;
  score: string | null;
  victory: boolean | null;
  location: string | null;
};

type ViewFilter = "all" | "upcoming" | "past";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}


function isUpcoming(iso: string) {
  return new Date(iso) >= new Date();
}

function groupByTeam(
  matches: Match[],
  teamMap: Map<string, string>
): { teamId: string; teamName: string; matches: Match[] }[] {
  const map = new Map<string, { teamId: string; teamName: string; matches: Match[] }>();
  for (const m of matches) {
    const key = m.team_id;
    if (!map.has(key)) {
      map.set(key, { teamId: key, teamName: teamMap.get(key) ?? "Équipe", matches: [] });
    }
    map.get(key)!.matches.push(m);
  }
  return Array.from(map.values()).sort((a, b) => a.teamName.localeCompare(b.teamName, "fr"));
}

// ─── Match Card ───────────────────────────────────────────────────────────────

function MatchCard({
  match,
  teamName,
  clubId,
}: {
  match: Match;
  teamName: string;
  clubId: string;
}) {
  const upcoming = isUpcoming(match.date);
  const hasResult = !upcoming && match.score;

  return (
    <Link
      to={`/club/${clubId}/matches/${match.id}`}
      className="group flex items-center gap-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 hover:border-gray-300 dark:hover:border-gray-700 transition"
    >
      {/* Date column */}
      <div className="shrink-0 w-14 text-center">
        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-none">
          {new Date(match.date).getDate().toString().padStart(2, "0")}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">
          {new Date(match.date).toLocaleDateString("fr-FR", { month: "short" })}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500">
          {formatTime(match.date)}
        </div>
      </div>

      {/* Divider */}
      <div className="w-px h-12 bg-gray-100 dark:bg-gray-800 shrink-0" />

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
            {teamName}
          </span>
          {match.location && (
            <span
              className={cx(
                "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full",
                match.location === "domicile"
                  ? "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                  : "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300"
              )}
            >
              <MapPin className="h-3 w-3" />
              {match.location === "domicile" ? "Domicile" : "Extérieur"}
            </span>
          )}
          {upcoming && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">
              À venir
            </span>
          )}
        </div>

        <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
          vs {match.opponent || "Adversaire inconnu"}
        </div>

        {hasResult && match.score && (
          <div className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Score : <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{match.score}</span>
          </div>
        )}
      </div>

      {/* Result badge */}
      <div className="shrink-0 flex items-center gap-2">
        {hasResult && match.victory !== null && (
          <span
            className={cx(
              "inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full",
              match.victory
                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
            )}
          >
            <Trophy className="h-3 w-3" />
            {match.victory ? "Victoire" : "Défaite"}
          </span>
        )}
        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition" />
      </div>
    </Link>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ClubMatches() {
  const { clubId } = useParams<{ clubId: string }>();

  const [teams, setTeams] = useState<Team[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [viewFilter, setViewFilter] = useState<ViewFilter>("all");

  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const teamsRes = await supabase
          .from("teams")
          .select("id, team_name")
          .eq("club_id", clubId)
          .order("team_name", { ascending: true });

        if (teamsRes.error) throw teamsRes.error;

        const teamRows = (teamsRes.data || []) as Team[];
        const teamIds = teamRows.map((t) => t.id);

        let matchRows: Match[] = [];
        if (teamIds.length > 0) {
          const matchesRes = await supabase
            .from("matches")
            .select("id, date, team_id, opponent, score, victory, location")
            .in("team_id", teamIds)
            .order("date", { ascending: false });

          if (matchesRes.error) throw matchesRes.error;
          matchRows = (matchesRes.data || []) as Match[];
        }

        if (!cancelled) {
          setTeams(teamRows);
          setMatches(matchRows);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Impossible de charger les matchs.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [clubId]);

  const teamMap = useMemo(() => {
    const m = new Map<string, string>();
    teams.forEach((t) => m.set(t.id, t.team_name?.trim() || "Équipe"));
    return m;
  }, [teams]);

  const filtered = useMemo(() => {
    let list = matches;

    if (viewFilter === "upcoming") {
      list = list.filter((m) => isUpcoming(m.date));
      list = [...list].reverse();
    } else if (viewFilter === "past") {
      list = list.filter((m) => !isUpcoming(m.date));
    }

    return list;
  }, [matches, viewFilter]);

  const grouped = useMemo(() => groupByTeam(filtered, teamMap), [filtered, teamMap]);

  const stats = useMemo(() => {
    const past = matches.filter((m) => !isUpcoming(m.date) && m.victory !== null);
    const wins = past.filter((m) => m.victory).length;
    return {
      total: matches.length,
      upcoming: matches.filter((m) => isUpcoming(m.date)).length,
      past: past.length,
      wins,
      losses: past.length - wins,
    };
  }, [matches]);

  const viewTabs: { key: ViewFilter; label: string }[] = [
    { key: "all", label: "Tous" },
    { key: "upcoming", label: "À venir" },
    { key: "past", label: "Passés" },
  ];

  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
        <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement des matchs…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300">
        <div className="inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center sm:text-left">
        <div className="text-sm text-gray-500 dark:text-gray-400">Club</div>
        <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center justify-center gap-2 sm:justify-start">
          <CalendarDays className="h-5 w-5 text-gray-500" />
          Matchs du club
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {stats.total} match{stats.total !== 1 ? "s" : ""} au total — {stats.upcoming} à venir, {stats.past} joués
        </div>
      </div>

      {/* Stat cards */}
      {matches.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total", value: stats.total, color: "text-gray-900 dark:text-gray-100" },
            { label: "À venir", value: stats.upcoming, color: "text-indigo-700 dark:text-indigo-300" },
            { label: "Victoires", value: stats.wins, color: "text-green-700 dark:text-green-300" },
            { label: "Défaites", value: stats.losses, color: "text-red-700 dark:text-red-300" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <div className={cx("text-2xl font-bold", s.color)}>{s.value}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* View tabs */}
        <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1 gap-1">
          {viewTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setViewFilter(t.key)}
              className={cx(
                "px-3 py-1.5 rounded-lg text-sm transition",
                viewFilter === t.key
                  ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                  : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

      </div>

      {/* Match list */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-12 flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500">
          <CalendarDays className="h-8 w-8" />
          <div className="text-sm">
            {matches.length === 0
              ? "Aucun match enregistré pour ce club."
              : "Aucun match pour ces filtres."}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map((group) => (
            <div key={group.teamId}>
              <div className="flex items-center gap-3 mb-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {group.teamName}
                </div>
                <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
                <div className="text-xs text-gray-400 dark:text-gray-500">
                  {group.matches.length} match{group.matches.length !== 1 ? "s" : ""}
                </div>
              </div>

              <div className="space-y-2">
                {group.matches.map((match: Match) => (
                  <MatchCard
                    key={match.id}
                    match={match}
                    teamName={group.teamName}
                    clubId={clubId!}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
